import {
  VIDEO_BACKGROUND_EFFECT_BLUR,
  VIDEO_BACKGROUND_EFFECT_NONE,
  createVideoBackgroundBlurProcessor,
} from './videoBackgroundEffects.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const textEncoder = new TextEncoder();
const MEDIA_E2EE_VERSION = 2;
const MEDIA_E2EE_MAGIC = new Uint8Array([0x4e, 0x47, 0x56, 0x43]); // NGVC
const MEDIA_E2EE_HEADER_LENGTH = MEDIA_E2EE_MAGIC.length + 1 + 8;
const MEDIA_E2EE_AES_GCM_TAG_BITS = 128;
const CALL_MODE_VIDEO = 'video';
const AUDIO_FRAME_CLEAR_PREFIX_BYTES = 1;
const VIDEO_DELTA_FRAME_CLEAR_PREFIX_BYTES = 3;
const VIDEO_KEY_FRAME_CLEAR_PREFIX_BYTES = 10;

const canUseWebRtc = () =>
  typeof window !== 'undefined' &&
  typeof window.RTCPeerConnection === 'function' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

const traceVoiceCallMediaE2ee = (event, details = {}) => {
  if (typeof window === 'undefined') return;
  try {
    const bucket = Array.isArray(window.__MESSENGER_V2_CALL_MEDIA_TRACE__)
      ? window.__MESSENGER_V2_CALL_MEDIA_TRACE__
      : [];
    bucket.push({
      at: new Date().toISOString(),
      event,
      ...details,
    });
    window.__MESSENGER_V2_CALL_MEDIA_TRACE__ = bucket.slice(-100);
  } catch {}
};

const canUseVoiceCallMediaE2ee = () => {
  try {
    return (
      canUseWebRtc() &&
      typeof TransformStream === 'function' &&
      typeof crypto !== 'undefined' &&
      typeof crypto.getRandomValues === 'function' &&
      typeof crypto.subtle?.importKey === 'function' &&
      typeof crypto.subtle?.deriveKey === 'function' &&
      typeof crypto.subtle?.deriveBits === 'function' &&
      typeof crypto.subtle?.digest === 'function' &&
      typeof crypto.subtle?.encrypt === 'function' &&
      typeof crypto.subtle?.decrypt === 'function' &&
      typeof window.RTCRtpSender?.prototype?.createEncodedStreams === 'function' &&
      typeof window.RTCRtpReceiver?.prototype?.createEncodedStreams === 'function'
    );
  } catch {
    return false;
  }
};

const bytesToBase64 = (bytes) => {
  const source = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  let binary = '';
  source.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const base64ToBytes = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return null;
  try {
    const binary = atob(normalized);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    return null;
  }
};

const writeUint64 = (target, offset, value) => {
  const normalized = Math.max(0, Math.trunc(Number(value) || 0));
  const high = Math.floor(normalized / 0x100000000);
  const low = normalized >>> 0;
  const view = new DataView(target.buffer, target.byteOffset, target.byteLength);
  view.setUint32(offset, high, false);
  view.setUint32(offset + 4, low, false);
};

const readUint64 = (source, offset) => {
  const view = new DataView(source.buffer, source.byteOffset, source.byteLength);
  return view.getUint32(offset, false) * 0x100000000 + view.getUint32(offset + 4, false);
};

const hasMediaFrameMagic = (source, offset = 0) => {
  const normalizedOffset = Math.max(0, Math.trunc(Number(offset) || 0));
  if (!source || source.length < normalizedOffset + MEDIA_E2EE_HEADER_LENGTH) return false;
  for (let index = 0; index < MEDIA_E2EE_MAGIC.length; index += 1) {
    if (source[normalizedOffset + index] !== MEDIA_E2EE_MAGIC[index]) return false;
  }
  return source[normalizedOffset + MEDIA_E2EE_MAGIC.length] === MEDIA_E2EE_VERSION;
};

const resolveEncodedFrameClearPrefixLength = (frame, input, kind = null) => {
  const byteLength = input?.byteLength ?? input?.length ?? 0;
  const normalizedKind = toStringOrNull(kind ?? frame?.kind ?? frame?.trackKind)?.toLowerCase();
  if (normalizedKind === 'audio') {
    return Math.min(AUDIO_FRAME_CLEAR_PREFIX_BYTES, byteLength);
  }
  if (normalizedKind !== 'video') return 0;
  const frameType = toStringOrNull(frame?.type)?.toLowerCase();
  const clearBytes =
    frameType === 'key'
      ? VIDEO_KEY_FRAME_CLEAR_PREFIX_BYTES
      : VIDEO_DELTA_FRAME_CLEAR_PREFIX_BYTES;
  return Math.min(clearBytes, byteLength);
};

const createMediaFrameNonce = (nonceBase, counter) => {
  const nonce = new Uint8Array(nonceBase);
  const counterBytes = new Uint8Array(8);
  writeUint64(counterBytes, 0, counter);
  for (let index = 0; index < counterBytes.length; index += 1) {
    nonce[nonce.length - counterBytes.length + index] ^= counterBytes[index];
  }
  return nonce;
};

const deriveMediaFrameMaterial = async ({ secretB64, callId, label } = {}) => {
  const secret = base64ToBytes(secretB64);
  const normalizedCallId = toStringOrNull(callId);
  const normalizedLabel = toStringOrNull(label);
  if (!secret || !normalizedCallId || !normalizedLabel) {
    throw new Error('Call media encryption key is missing');
  }
  const baseKey = await crypto.subtle.importKey('raw', secret, 'HKDF', false, [
    'deriveBits',
    'deriveKey',
  ]);
  const salt = textEncoder.encode(`messenger-v2-call:${normalizedCallId}`);
  const [key, nonceBits] = await Promise.all([
    crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt,
        info: textEncoder.encode(`media-frame-key-v${MEDIA_E2EE_VERSION}:${normalizedLabel}`),
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    ),
    crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt,
        info: textEncoder.encode(`media-frame-nonce-v${MEDIA_E2EE_VERSION}:${normalizedLabel}`),
      },
      baseKey,
      96,
    ),
  ]);
  return {
    key,
    nonceBase: new Uint8Array(nonceBits),
  };
};

const encryptEncodedFrame = async ({ frame, material, nextCounter, kind = null }) => {
  const input = frame?.data ? new Uint8Array(frame.data) : new Uint8Array();
  if (!input.length) return frame;

  const counter = nextCounter();
  const clearPrefixLength = resolveEncodedFrameClearPrefixLength(frame, input, kind);
  const clearPrefix = clearPrefixLength > 0 ? input.slice(0, clearPrefixLength) : null;
  const payload = clearPrefixLength > 0 ? input.slice(clearPrefixLength) : input;
  if (!payload.length) return frame;
  const header = new Uint8Array(MEDIA_E2EE_HEADER_LENGTH);
  header.set(MEDIA_E2EE_MAGIC, 0);
  header[MEDIA_E2EE_MAGIC.length] = MEDIA_E2EE_VERSION;
  writeUint64(header, MEDIA_E2EE_MAGIC.length + 1, counter);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: createMediaFrameNonce(material.nonceBase, counter),
        additionalData: header,
        tagLength: MEDIA_E2EE_AES_GCM_TAG_BITS,
      },
      material.key,
      payload,
    ),
  );
  const output = new Uint8Array(clearPrefixLength + header.length + ciphertext.length);
  if (clearPrefix) output.set(clearPrefix, 0);
  output.set(header, clearPrefixLength);
  output.set(ciphertext, clearPrefixLength + header.length);
  frame.data = output.buffer;
  return frame;
};

const decryptEncodedFrame = async ({ frame, material, kind = null }) => {
  const input = frame?.data ? new Uint8Array(frame.data) : new Uint8Array();
  if (!input.length) return frame;
  const expectedClearPrefixLength = resolveEncodedFrameClearPrefixLength(frame, input, kind);
  const clearPrefixLength = hasMediaFrameMagic(input, expectedClearPrefixLength)
    ? expectedClearPrefixLength
    : hasMediaFrameMagic(input, 0)
      ? 0
      : -1;
  if (clearPrefixLength < 0) {
    throw new Error('Call encrypted media frame header is invalid');
  }
  const header = input.slice(clearPrefixLength, clearPrefixLength + MEDIA_E2EE_HEADER_LENGTH);
  const counter = readUint64(header, MEDIA_E2EE_MAGIC.length + 1);
  const plaintext = new Uint8Array(
    await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: createMediaFrameNonce(material.nonceBase, counter),
        additionalData: header,
        tagLength: MEDIA_E2EE_AES_GCM_TAG_BITS,
      },
      material.key,
      input.slice(clearPrefixLength + MEDIA_E2EE_HEADER_LENGTH),
    ),
  );
  if (clearPrefixLength <= 0) {
    frame.data = plaintext.buffer;
    return frame;
  }
  const output = new Uint8Array(clearPrefixLength + plaintext.length);
  output.set(input.slice(0, clearPrefixLength), 0);
  output.set(plaintext, clearPrefixLength);
  frame.data = output.buffer;
  return frame;
};

const pipeEncodedStream = ({ readable, writable, transform }) => {
  if (!readable || !writable || typeof readable.pipeThrough !== 'function') {
    throw new Error('Call encoded streams are unavailable');
  }
  readable.pipeThrough(transform).pipeTo(writable).catch(() => null);
};

const normalizeMediaEncryption = ({ callId, direction, mediaEncryption } = {}) => {
  const required = mediaEncryption?.required !== false;
  const role =
    mediaEncryption?.role === 'responder' || direction === 'incoming' ? 'responder' : 'initiator';
  const canGenerateSecret =
    typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function';
  const secretB64 =
    toStringOrNull(mediaEncryption?.secretB64 ?? mediaEncryption?.secret_b64 ?? null) ||
    (role === 'initiator' && canGenerateSecret
      ? bytesToBase64(crypto.getRandomValues(new Uint8Array(32)))
      : null);

  return {
    version: MEDIA_E2EE_VERSION,
    required,
    role,
    secretB64,
    callId: toStringOrNull(callId),
  };
};

const normalizeSdp = (value) => {
  const raw = toStringOrNull(value);
  if (!raw) return null;
  const normalized = raw.replace(/\r\n|\r|\n/g, '\r\n');
  return normalized.endsWith('\r\n') ? normalized : `${normalized}\r\n`;
};

const withoutSsrcAttributeLines = (sdp) =>
  normalizeSdp(
    String(sdp || '')
      .replace(/\r\n|\r|\n/g, '\n')
      .split('\n')
      .filter((line) => !/^a=ssrc(?:-group)?:/i.test(line.trim()))
      .join('\r\n'),
  );

const normalizeSessionDescription = (description = null) => {
  if (!description || typeof description !== 'object') return null;
  const type = toStringOrNull(description.type);
  const sdp = normalizeSdp(description.sdp);
  return type && sdp ? { type, sdp } : null;
};

const toSignalSessionDescription = (description = null) => {
  const normalized = normalizeSessionDescription(
    description?.toJSON?.() || description,
  );
  if (!normalized) return null;
  const sanitizedSdp = withoutSsrcAttributeLines(normalized.sdp);
  return {
    ...normalized,
    sdp: sanitizedSdp || normalized.sdp,
  };
};

const normalizeIceCandidate = (candidate = null) => {
  if (!candidate || typeof candidate !== 'object') return null;
  const rawCandidate = toStringOrNull(candidate.candidate);
  if (!rawCandidate) return null;
  return {
    candidate: rawCandidate,
    sdpMid: candidate.sdpMid ?? null,
    sdpMLineIndex:
      Number.isFinite(Number(candidate.sdpMLineIndex))
        ? Number(candidate.sdpMLineIndex)
        : null,
    usernameFragment: candidate.usernameFragment ?? null,
  };
};

export class MessengerV2VoiceCallSession {
  constructor({
    callId,
    conversationId,
    direction,
    mode = 'voice',
    iceServers = [],
    signal,
    mediaEncryption = null,
    onLocalStream,
    onRemoteStream,
    onConnectionState,
  } = {}) {
    if (!canUseWebRtc()) {
      throw new Error('Calls are not supported by this browser');
    }
    this.callId = toStringOrNull(callId);
    this.conversationId = toStringOrNull(conversationId);
    this.direction = direction === 'incoming' ? 'incoming' : 'outgoing';
    this.mode = mode === CALL_MODE_VIDEO ? CALL_MODE_VIDEO : 'voice';
    this.signal = typeof signal === 'function' ? signal : () => {};
    this.mediaEncryption = normalizeMediaEncryption({
      callId: this.callId,
      direction: this.direction,
      mediaEncryption,
    });
    if (this.mediaEncryption.required && !canUseVoiceCallMediaE2ee()) {
      throw new Error('Call PQ media encryption is not supported by this browser');
    }
    if (this.mediaEncryption.required && !this.mediaEncryption.secretB64) {
      throw new Error('Call PQ media encryption key is missing');
    }
    this.mediaEncryptionKeysPromise = null;
    this.encryptedSenders = new WeakSet();
    this.encryptedReceivers = new WeakSet();
    this.onLocalStream = typeof onLocalStream === 'function' ? onLocalStream : () => {};
    this.onRemoteStream = typeof onRemoteStream === 'function' ? onRemoteStream : () => {};
    this.onConnectionState =
      typeof onConnectionState === 'function' ? onConnectionState : () => {};
    this.remoteDescriptionReady = false;
    this.pendingIceCandidates = [];
    this.closed = false;
    this.localSourceStream = null;
    this.localStream = null;
    this.audioSender = null;
    this.videoSender = null;
    this.videoBackgroundEffect = VIDEO_BACKGROUND_EFFECT_NONE;
    this.videoBackgroundProcessor = null;
    this.remoteStream = new MediaStream();
    this.peerConnection = new RTCPeerConnection({
      iceServers,
      encodedInsertableStreams: this.mediaEncryption.required === true,
    });
    this.peerConnection.onicecandidate = (event) => {
      const candidate = normalizeIceCandidate(event.candidate);
      if (candidate) {
        this.signal('ice', { candidate });
      }
    };
    this.peerConnection.ontrack = (event) => {
      if (this.mediaEncryption.required && event.receiver) {
        void this.setupReceiverEncryption(event.receiver).catch(() => null);
      }
      event.streams?.[0]?.getTracks?.().forEach((track) => {
        if (!this.remoteStream.getTracks().some((current) => current.id === track.id)) {
          this.remoteStream.addTrack(track);
        }
      });
      if (!event.streams?.length && event.track) {
        this.remoteStream.addTrack(event.track);
      }
      this.onRemoteStream(this.remoteStream);
    };
    this.peerConnection.onconnectionstatechange = () => {
      this.onConnectionState(this.peerConnection.connectionState);
    };
    this.peerConnection.oniceconnectionstatechange = () => {
      this.onConnectionState(this.peerConnection.iceConnectionState);
    };
  }

  getMediaEncryptionOffer() {
    return {
      version: this.mediaEncryption.version,
      required: this.mediaEncryption.required,
      algorithm: 'MLS-PQ+AES-256-GCM encoded audio/video frames',
      secretB64: this.mediaEncryption.secretB64,
    };
  }

  getMediaEncryptionAnswer() {
    return {
      version: this.mediaEncryption.version,
      accepted: this.mediaEncryption.required === true,
      algorithm: 'MLS-PQ+AES-256-GCM encoded audio/video frames',
    };
  }

  async ensureMediaEncryptionKeys() {
    if (!this.mediaEncryption.required) return null;
    if (!this.mediaEncryptionKeysPromise) {
      const outboundLabel =
        this.mediaEncryption.role === 'initiator'
          ? 'initiator-to-responder'
          : 'responder-to-initiator';
      const inboundLabel =
        this.mediaEncryption.role === 'initiator'
          ? 'responder-to-initiator'
          : 'initiator-to-responder';
      this.mediaEncryptionKeysPromise = Promise.all([
          deriveMediaFrameMaterial({
            secretB64: this.mediaEncryption.secretB64,
            callId: this.callId,
            label: outboundLabel,
          }),
          deriveMediaFrameMaterial({
            secretB64: this.mediaEncryption.secretB64,
            callId: this.callId,
            label: inboundLabel,
          }),
      ]).then(([sendMaterial, receiveMaterial]) => ({
        sendMaterial,
        receiveMaterial,
        outboundLabel,
        inboundLabel,
      }));
    }
    return this.mediaEncryptionKeysPromise;
  }

  async setupSenderEncryption(sender) {
    if (!this.mediaEncryption.required || !sender || this.encryptedSenders.has(sender)) {
      return;
    }
    this.encryptedSenders.add(sender);
    const streams = sender.createEncodedStreams?.();
    const { sendMaterial } = await this.ensureMediaEncryptionKeys();
    const kind = toStringOrNull(sender?.track?.kind);
    pipeEncodedStream({
      readable: streams?.readable,
      writable: streams?.writable,
      transform: new TransformStream({
        transform: async (frame, controller) => {
          try {
            controller.enqueue(
              await encryptEncodedFrame({
                frame,
                material: sendMaterial,
                kind,
                nextCounter: () => {
                  this.outboundFrameCounter = (this.outboundFrameCounter || 0) + 1;
                  return this.outboundFrameCounter;
                },
              }),
            );
          } catch (error) {
            traceVoiceCallMediaE2ee('frame_encrypt_failed', {
              error: String(error?.message || error),
            });
          }
        },
      }),
    });
  }

  async setupReceiverEncryption(receiver) {
    if (!this.mediaEncryption.required || !receiver || this.encryptedReceivers.has(receiver)) {
      return;
    }
    this.encryptedReceivers.add(receiver);
    const streams = receiver.createEncodedStreams?.();
    const { receiveMaterial } = await this.ensureMediaEncryptionKeys();
    const kind = toStringOrNull(receiver?.track?.kind);
    pipeEncodedStream({
      readable: streams?.readable,
      writable: streams?.writable,
      transform: new TransformStream({
        transform: async (frame, controller) => {
          try {
            controller.enqueue(
              await decryptEncodedFrame({
                frame,
                material: receiveMaterial,
                kind,
              }),
            );
          } catch (error) {
            traceVoiceCallMediaE2ee('frame_decrypt_failed', {
              error: String(error?.message || error),
            });
          }
        },
      }),
    });
  }

  async setupCurrentReceiverEncryption() {
    if (!this.mediaEncryption.required) return;
    const receivers =
      typeof this.peerConnection.getReceivers === 'function'
        ? this.peerConnection.getReceivers()
        : [];
    await Promise.all(
      receivers
        .filter((receiver) => ['audio', 'video'].includes(String(receiver?.track?.kind || '')))
        .map((receiver) => this.setupReceiverEncryption(receiver)),
    );
  }

  async setRemoteSessionDescription(description) {
    try {
      await this.peerConnection.setRemoteDescription(description);
    } catch (error) {
      const fallbackSdp = withoutSsrcAttributeLines(description.sdp);
      if (!fallbackSdp || fallbackSdp === description.sdp) {
        throw error;
      }
      await this.peerConnection.setRemoteDescription({
        ...description,
        sdp: fallbackSdp,
      });
    }
    await this.setupCurrentReceiverEncryption();
  }

  async prepareLocalMedia() {
    if (this.localStream) return this.localStream;
    this.localSourceStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video:
        this.mode === CALL_MODE_VIDEO
          ? {
              frameRate: { ideal: 24, max: 30 },
              facingMode: { ideal: 'user' },
              resizeMode: { ideal: 'none' },
            }
          : false,
    });
    this.localStream = this.localSourceStream;
    const encryptionSetups = [];
    this.localSourceStream.getTracks().forEach((track) => {
      const sender = this.peerConnection.addTrack(track, this.localSourceStream);
      if (track.kind === 'audio') this.audioSender = sender;
      if (track.kind === 'video') this.videoSender = sender;
      encryptionSetups.push(this.setupSenderEncryption(sender));
    });
    await Promise.all(encryptionSetups);
    this.onLocalStream(this.localStream);
    return this.localStream;
  }

  async prepareLocalAudio() {
    return this.prepareLocalMedia();
  }

  stopVideoBackgroundProcessor() {
    const processor = this.videoBackgroundProcessor;
    this.videoBackgroundProcessor = null;
    if (processor) {
      try {
        processor.stop?.();
      } catch {}
    }
  }

  async setVideoBackgroundEffect(effect) {
    const nextEffect =
      effect === VIDEO_BACKGROUND_EFFECT_BLUR
        ? VIDEO_BACKGROUND_EFFECT_BLUR
        : VIDEO_BACKGROUND_EFFECT_NONE;
    if (this.mode !== CALL_MODE_VIDEO) {
      return VIDEO_BACKGROUND_EFFECT_NONE;
    }
    await this.prepareLocalMedia();
    if (nextEffect === this.videoBackgroundEffect) {
      return this.videoBackgroundEffect;
    }

    const rawVideoTrack = this.localSourceStream?.getVideoTracks?.()[0] || null;
    if (!rawVideoTrack || !this.videoSender) {
      throw new Error('Camera track is unavailable');
    }

    if (nextEffect === VIDEO_BACKGROUND_EFFECT_NONE) {
      await this.videoSender.replaceTrack(rawVideoTrack);
      this.stopVideoBackgroundProcessor();
      this.localStream = this.localSourceStream;
      this.videoBackgroundEffect = VIDEO_BACKGROUND_EFFECT_NONE;
      this.onLocalStream(this.localStream);
      return this.videoBackgroundEffect;
    }

    const processor = await createVideoBackgroundBlurProcessor({
      sourceStream: this.localSourceStream,
    });
    try {
      processor.videoTrack.enabled = rawVideoTrack.enabled;
      await this.videoSender.replaceTrack(processor.videoTrack);
    } catch (error) {
      processor.stop?.();
      throw error;
    }

    this.stopVideoBackgroundProcessor();
    this.videoBackgroundProcessor = processor;
    this.localStream = processor.stream;
    this.videoBackgroundEffect = VIDEO_BACKGROUND_EFFECT_BLUR;
    this.onLocalStream(this.localStream);
    return this.videoBackgroundEffect;
  }

  async createOffer() {
    await this.prepareLocalMedia();
    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: this.mode === CALL_MODE_VIDEO,
    });
    await this.peerConnection.setLocalDescription(offer);
    return toSignalSessionDescription(this.peerConnection.localDescription || offer);
  }

  async acceptOffer(offer) {
    const description = normalizeSessionDescription(offer);
    if (!description) throw new Error('Invalid call offer');
    await this.prepareLocalMedia();
    await this.setRemoteSessionDescription(description);
    this.remoteDescriptionReady = true;
    await this.flushPendingIceCandidates();
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return toSignalSessionDescription(this.peerConnection.localDescription || answer);
  }

  async applyAnswer(answer) {
    const description = normalizeSessionDescription(answer);
    if (!description) throw new Error('Invalid call answer');
    await this.setRemoteSessionDescription(description);
    this.remoteDescriptionReady = true;
    await this.flushPendingIceCandidates();
  }

  async addIceCandidate(candidate) {
    const normalized = normalizeIceCandidate(candidate);
    if (!normalized || this.closed) return;
    if (!this.remoteDescriptionReady) {
      this.pendingIceCandidates.push(normalized);
      return;
    }
    try {
      await this.peerConnection.addIceCandidate(normalized);
    } catch {}
  }

  async flushPendingIceCandidates() {
    const pending = this.pendingIceCandidates.splice(0);
    for (const candidate of pending) {
      await this.addIceCandidate(candidate);
    }
  }

  setMuted(muted) {
    const nextMuted = muted === true;
    this.localSourceStream?.getAudioTracks?.().forEach((track) => {
      track.enabled = !nextMuted;
    });
    this.localStream?.getAudioTracks?.().forEach((track) => {
      track.enabled = !nextMuted;
    });
    return nextMuted;
  }

  setCameraEnabled(enabled) {
    const nextEnabled = enabled !== false;
    this.localSourceStream?.getVideoTracks?.().forEach((track) => {
      track.enabled = nextEnabled;
    });
    this.localStream?.getVideoTracks?.().forEach((track) => {
      track.enabled = nextEnabled;
    });
    return nextEnabled;
  }

  close() {
    this.closed = true;
    try {
      this.peerConnection?.close?.();
    } catch {}
    this.stopVideoBackgroundProcessor();
    const localTracks = new Set([
      ...(this.localSourceStream?.getTracks?.() || []),
      ...(this.localStream?.getTracks?.() || []),
    ]);
    localTracks.forEach((track) => {
      try {
        track.stop();
      } catch {}
    });
    this.remoteStream?.getTracks?.().forEach((track) => {
      try {
        track.stop();
      } catch {}
    });
    this.localSourceStream = null;
    this.localStream = null;
  }
}

export const isMessengerV2VoiceCallSupported = canUseWebRtc;
export const isMessengerV2VoiceCallPqE2eeSupported = canUseVoiceCallMediaE2ee;
